import styled from '@emotion/styled'

import { Dayjs } from '@bemlo/date'
import { DynamicShiftStatus } from '@bemlo/enums'
import { shiftWorkDuration } from '@bemlo/schedule'

import { Color, theme } from '../constants'
import { Badge } from '../elements/Badge'
import { Clickable } from '../elements/Clickable'
import { Flex } from '../elements/Flex'
import { STATUS_COLOR_MAP } from '../elements/ShiftCard/useShiftCardColors'
import { Text } from '../elements/Text'
import { useTranslations } from '../translate'

const StyledClickable = styled(Clickable)({
  display: 'flex',
  boxShadow: theme.boxShadow.normal,
  borderRadius: theme.border.radius.normal,
  backgroundColor: Color.WHITE,

  '&:hover': {
    filter: 'brightness(98%)',
  },

  '&:active': {
    filter: 'brightness(95%)',
  },
})

const LEFT_BORDER_WIDTH = theme.border.radius.normal

const colors = STATUS_COLOR_MAP[DynamicShiftStatus.BOOKED]

const StatusBorder = styled.div({
  borderBottomLeftRadius: theme.border.radius.normal,
  borderTopLeftRadius: theme.border.radius.normal,
  backgroundColor: colors.text,
  width: LEFT_BORDER_WIDTH,
})

const Left = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderTop: `1px solid ${colors.borderOuter}`,
  borderBottom: `1px solid ${colors.borderOuter}`,
  borderRight: `1px solid ${colors.borderInner}`,
  width: 50,
  backgroundColor: colors.background,
  color: colors.text,
})

const Right = styled.div({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[1],
  borderTop: theme.border.style.thin,
  borderRight: theme.border.style.thin,
  borderBottom: theme.border.style.thin,
  borderTopRightRadius: theme.border.radius.normal,
  borderBottomRightRadius: theme.border.radius.normal,
  padding: theme.spacing[3],
  overflow: 'hidden',
})

const TimeRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[3],
  containerType: 'inline-size',
})

const TimeText = styled(Text)`
  @container (max-width: 300px) {
    font-size: ${theme.font.size[5]}px;
  }
`

type Unit = {
  address: string
  name: string
}

type ScheduleResource = {
  name: string
}

type Shift = {
  unit: Unit
  scheduleResource: ScheduleResource | undefined
}

type Timesheet = {
  startDateTime: Dayjs
  endDateTime: Dayjs
  breakTime: number
  shift: Shift
}

type TimesheetCardProps = {
  className?: string
  timesheet: Timesheet
  pricing?: string
  onClick: () => void
}

export const TimesheetCard = ({
  className,
  timesheet,
  pricing,
  onClick,
}: TimesheetCardProps) => {
  const t = useTranslations()

  const duration = shiftWorkDuration({
    startTime: timesheet.startDateTime.format('HH:mm'),
    endTime: timesheet.endDateTime.format('HH:mm'),
    breakTime: timesheet.breakTime,
    date: timesheet.startDateTime.format('YYYY-MM-DD'),
    onCallTime: null,
  }).shift

  return (
    <StyledClickable className={className} onClick={onClick}>
      <StatusBorder />

      <Left>
        <Text size={5}>
          {timesheet.startDateTime.format('MMM').toUpperCase()}
        </Text>
        <Text size={2} fontWeight={theme.font.weight.bold}>
          {timesheet.startDateTime.date()}
        </Text>
      </Left>

      <Right>
        <TimeRow>
          <TimeText size={3} fontWeight={theme.font.weight.bold}>
            {timesheet.startDateTime.format('ddd')},{' '}
            {timesheet.startDateTime.formatTimeRange(timesheet.endDateTime)}
          </TimeText>
          <Text color={Color.MEDIUM_BLUE} size={5}>
            {t.$date.formatDuration({ hours: duration.asHours() })}
          </Text>
        </TimeRow>

        <Flex gap={2} items="end">
          <Flex col gap={1} styles={{ overflow: 'hidden' }}>
            <Text
              size={6.5}
              fontWeight={theme.font.weight.bold}
              color={Color.DARKER_GREY}
              noWrap
            >
              {timesheet.shift.scheduleResource?.name && (
                <>
                  {timesheet.shift.scheduleResource?.name}
                  &nbsp;&nbsp;&bull;&nbsp;&nbsp;
                </>
              )}
              {timesheet.shift.unit.name}
            </Text>

            <Text size={6.5} color={Color.DARK_GREY} noWrap>
              {timesheet.shift.unit.address}
            </Text>
          </Flex>

          {pricing && (
            <Badge styles={{ ml: 'auto' }} size="sm">
              {pricing}
            </Badge>
          )}
        </Flex>
      </Right>
    </StyledClickable>
  )
}
